import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { OverlayTemplateConfig, OverlayTemplateResponse, Tendv2ContentItem } from '../../api/tendApi';
import { Logo } from '../../api/logoApi';
import OverlayPreview from './OverlayPreview';
import FeedbackButton from './FeedbackButton';

interface TemplateCardProps {
  template: OverlayTemplateResponse;
  contentItem: Tendv2ContentItem;
  brandName: string;
  brandLogo: Logo | null;
  brandId?: number | null;
  logoDisplayMode: string;
  isSelected: boolean;
  isDefault: boolean;
  isSuperAdmin: boolean;
  localConfig: OverlayTemplateConfig;
  localName: string;
  localDescription: string;
  isDirty: boolean;
  onConfigChange: (updates: Partial<OverlayTemplateConfig>) => void;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onSelect?: () => void;
  onMakeDefault: () => void;
  onSave: () => void;
  onRevert: () => void;
  onCopy: () => void;
  onDelete: () => void;
  categoryBorderRadius?: number;
  categoryBgColor?: string;
  dragEnabled?: boolean;
  // Text overrides (respects caption mode)
  titleOverride?: string;
  subheaderOverride?: string;
  bodyOverride?: string;
  /** Solid fill for previews when the placeholder item has no media */
  previewFallbackBgColor?: string | null;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  contentItem,
  brandName,
  brandLogo,
  brandId,
  logoDisplayMode,
  isSelected,
  isDefault,
  isSuperAdmin,
  localConfig,
  localName,
  localDescription,
  isDirty,
  onConfigChange,
  onNameChange,
  onDescriptionChange,
  onSelect,
  onMakeDefault,
  onSave,
  onRevert,
  onCopy,
  onDelete,
  categoryBorderRadius = 50,
  categoryBgColor = '#2563eb',
  dragEnabled = false,
  titleOverride,
  subheaderOverride,
  bodyOverride,
  previewFallbackBgColor,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);

  // Drag and drop setup
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: template.id, disabled: !dragEnabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  const isGlobal = template.brand_id === null;
  const canEdit = !isGlobal || isSuperAdmin;
  const canDelete = !isGlobal || isSuperAdmin;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative bg-white rounded-xl shadow-sm border-2 transition-all duration-200 overflow-hidden
        ${isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-300'}
        ${isHovered ? 'shadow-md' : ''}
        ${isDragging ? 'shadow-2xl' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Drag Handle - top right corner */}
      {dragEnabled && (
        <button
          {...attributes}
          {...listeners}
          className={`absolute top-2 right-2 z-30 p-1.5 rounded cursor-grab active:cursor-grabbing
            transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'}
            bg-gray-100 hover:bg-gray-200 text-gray-400 hover:text-gray-600`}
          title="Drag to reorder"
        >
          <svg className="w-4 h-4" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="4" cy="3" r="1.5" />
            <circle cx="4" cy="8" r="1.5" />
            <circle cx="4" cy="13" r="1.5" />
            <circle cx="10" cy="3" r="1.5" />
            <circle cx="10" cy="8" r="1.5" />
            <circle cx="10" cy="13" r="1.5" />
          </svg>
        </button>
      )}
      {/* Template Name - TOP (large, bold, editable) */}
      <div className="px-4 pt-4 pb-2">
        {isEditingName ? (
          <input
            type="text"
            value={localName}
            onChange={(e) => onNameChange(e.target.value)}
            onBlur={() => setIsEditingName(false)}
            onKeyDown={(e) => e.key === 'Enter' && setIsEditingName(false)}
            className="w-full text-xl font-bold text-gray-900 border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
        ) : (
          <div
            className={`text-xl font-bold text-gray-900 truncate ${canEdit ? 'cursor-text hover:bg-gray-50 rounded px-2 py-1 -mx-2 -my-1' : ''}`}
            onClick={() => canEdit && setIsEditingName(true)}
            title={canEdit ? 'Click to edit name' : localName}
          >
            {localName || 'Untitled Template'}
          </div>
        )}
      </div>

      {/* Image Preview with Overlay - MIDDLE */}
      <div className="px-4">
        <div className={`relative ${onSelect ? 'cursor-pointer' : ''}`} onClick={onSelect}>
          {/* Badges - positioned on the image */}
          <div className="absolute top-2 left-2 z-20 flex flex-col gap-1">
            {onSelect && isSelected && (
              <span className="bg-blue-500 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                SELECTED
              </span>
            )}
            {isDefault && isGlobal && (
              <span className="bg-emerald-600 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                GLOBAL DEFAULT
              </span>
            )}
            {isDefault && !isGlobal && (
              <span className="bg-green-500 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                DEFAULT
              </span>
            )}
            {isGlobal && !isDefault && (
              <span className="bg-yellow-500 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                GLOBAL
              </span>
            )}
            {isDirty && (
              <span className="bg-orange-500 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                UNSAVED
              </span>
            )}
          </div>

          <OverlayPreview
            contentItem={contentItem}
            config={localConfig}
            brandLogo={brandLogo}
            brandName={brandName}
            brandId={brandId}
            onConfigChange={onConfigChange}
            size="large"
            controlsEnabled={canEdit}
            safeZoneScope="bucket"
            logoSelectable={!isGlobal}
            logoDisplayMode={logoDisplayMode}
            categoryBorderRadius={categoryBorderRadius}
            categoryBgColor={categoryBgColor}
            titleOverride={titleOverride}
            subheaderOverride={subheaderOverride}
            bodyOverride={bodyOverride}
            fallbackBgColor={previewFallbackBgColor}
          />

          {/* Select overlay on hover (when not already selected) - only in select mode */}
          {onSelect && !isSelected && isHovered && (
            <div className="absolute inset-0 z-50 bg-blue-500/20 flex items-center justify-center pointer-events-none rounded-lg">
              <span className="bg-blue-600 text-white text-sm font-medium px-3 py-1.5 rounded-full shadow-lg">
                Click to Select
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Description and Actions - BOTTOM */}
      <div className="p-4">
        {/* Description - Editable */}
        {isEditingDescription ? (
          <textarea
            value={localDescription}
            onChange={(e) => onDescriptionChange(e.target.value)}
            onBlur={() => setIsEditingDescription(false)}
            className="w-full text-sm text-gray-600 border border-blue-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            rows={3}
            autoFocus
            placeholder="Explain how to use this template..."
          />
        ) : (
          <div
            className={`text-sm text-gray-600 leading-relaxed ${canEdit ? 'cursor-text hover:bg-gray-50 rounded px-2 py-1.5 -mx-2 -my-1.5 min-h-[60px]' : 'min-h-[60px]'}`}
            onClick={() => canEdit && setIsEditingDescription(true)}
            title={canEdit ? 'Click to edit description' : localDescription}
          >
            {localDescription || (canEdit ? 'Add description...' : 'No description')}
          </div>
        )}

        {/* Action buttons - Show on hover */}
        <div className={`flex items-center gap-1 mt-3 pt-3 border-t transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
          {/* Save button */}
          {isDirty && canEdit && (
            <button
              onClick={(e) => { e.stopPropagation(); onSave(); }}
              className="flex-1 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
              title="Save changes"
            >
              Save
            </button>
          )}

          {/* Revert button */}
          {isDirty && (
            <button
              onClick={(e) => { e.stopPropagation(); onRevert(); }}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              title="Revert changes"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
            </button>
          )}

          {/* Make default button - brand templates or global templates (SuperAdmin only) */}
          {!isDefault && (!isGlobal || isSuperAdmin) && (
            <button
              onClick={(e) => { e.stopPropagation(); onMakeDefault(); }}
              className="p-1.5 text-gray-400 hover:text-yellow-500 hover:bg-yellow-50 rounded transition-colors"
              title={isGlobal ? "Set as global default" : "Set as default"}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </button>
          )}

          {/* Copy button (for global templates). Superadmins need it too:
              global tiles never expose the logo picker (a universal template
              must not carry one brand's logo), so a per-brand logo variant
              starts from a brand copy — for everyone. */}
          {isGlobal && (
            <button
              onClick={(e) => { e.stopPropagation(); onCopy(); }}
              className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors"
              title="Copy to brand"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          )}

          <FeedbackButton
            entityType="overlay_template"
            entityKey={String(template.id)}
            brandId={brandId ?? undefined}
            tone="light"
          />
          {/* Delete button */}
          {canDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
              title={isGlobal ? 'Delete global template' : 'Hide template'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateCard;
